"use client";
import React, { useState, useEffect } from "react";
// 1. Import motion and AnimatePresence for top-tier animations
import { motion, AnimatePresence } from "framer-motion";

// --- Animation Variants ---

// For the header sliding in on page load
const headerVariants = {
  hidden: { y: "-100%" },
  visible: { y: "0%", transition: { duration: 0.5, ease: "easeOut" } },
};

// For the mobile menu backdrop
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

// For the mobile menu panel sliding in/out
const panelVariants = {
  hidden: { x: "-100%" },
  visible: { x: "0%", transition: { type: "spring", stiffness: 100, damping: 20 } },
  exit: { x: "-100%", transition: { duration: 0.3, ease: "easeInOut" } },
};

// For staggering the mobile menu items
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.2, // Start animating links after the panel is mostly in
    },
  },
};

// For each individual mobile menu item
const menuItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
};

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Collaborate", path: "/collaborate" },
  ];

  return (
    <>
      <motion.header
        className={`w-full fixed top-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? "bg-[#0E1720] shadow-xl" : "bg-transparent"}`}
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <motion.a
                href="/"
                className="flex items-center"
                whileHover={{ rotate: 15, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300, damping: 10 }}
              >
                <img src="/images/void.png" alt="Void Logo" className="h-10 w-auto" />
              </motion.a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:block">
              <div className="flex items-center space-x-8">
                {navLinks.map((item) => (
                  <motion.a
                    key={item.name}
                    href={item.path}
                    className="relative text-[#16a085] hover:text-white px-1 py-2 text-sm font-medium transition-colors duration-300 group"
                    whileHover={{ y: -3 }}
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#16a085] transition-all duration-300 group-hover:w-full"></span>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block ml-10">
              <motion.a
                href="/request-demo"
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-[#16a085] to-[#2ecc71] shadow-md"
                whileHover={{ y: -2, scale: 1.05, boxShadow: "0px 10px 20px rgba(0,0,0,0.2)" }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                Request Demo
                <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </motion.a>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden">
              <motion.button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                whileTap={{ scale: 0.9 }}
              >
                <span className="sr-only">Open main menu</span>
                <svg className={`block h-8 w-8 transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={!isMobileMenuOpen ? "M4 6h16M4 12h16M4 18h16" : "M6 18L18 6M6 6l12 12"} />
                </svg>
              </motion.button>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* --- REDESIGNED & ANIMATED MOBILE MENU --- */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 z-30 lg:hidden"
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
            
            {/* Menu Panel */}
            <motion.div
              id="mobile-menu"
              className="fixed top-0 left-0 h-full w-3/4 max-w-sm bg-[#0E1720] z-40 lg:hidden flex flex-col"
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-800">
                 <a href="/" onClick={() => setIsMobileMenuOpen(false)}>
                    <img src="/images/void.png" alt="Void Logo" className="h-8 w-auto" />
                 </a>
                 <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-400 hover:text-white rounded-md">
                   <span className="sr-only">Close menu</span>
                   <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
              </div>

              {/* Menu Links with Stagger Animation */}
              <motion.nav
                className="flex-grow p-4"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <div className="flex flex-col space-y-2">
                  {navLinks.map((item) => (
                    <motion.a
                      key={item.name}
                      href={item.path}
                      className="text-gray-300 hover:bg-gray-800 hover:text-white px-3 py-3 rounded-md text-lg font-medium"
                      variants={menuItemVariants}
                      onClick={() => setIsMobileMenuOpen(false)}
                      whileHover={{ x: 5 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {item.name}
                    </motion.a>
                  ))}
                </div>
              </motion.nav>
              
              {/* Menu Footer with CTA */}
              <div className="p-4 mt-auto border-t border-gray-800">
                 <a
                    href="/request-demo"
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-[#16a085] to-[#2ecc71] shadow-md transition-all duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Request Demo
                 </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}